import * as Clipboard from 'expo-clipboard';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity, StyleSheet } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import {
  GetMeDocument,
  useCompleteWebTutorialFlowsMutation,
  useCreateTeamInvitationMutation,
  WebTutorialFlow,
} from '@graphql/generated';
import { useHover } from '@hooks/useHover';
import useMe from '@hooks/useMe';
import theme from '@themes/theme';

interface TeamActionListProps {
  teamId: string;
  teamName: string;
  isOwnerOrAdmin?: boolean;
  setShowAddMembersModal: (show: boolean) => void;
}

const TeamActionList: React.FC<TeamActionListProps> = ({
  teamId,
  teamName,
  isOwnerOrAdmin,
  setShowAddMembersModal,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { me } = useMe();
  // Ghost action hover (TA-9005): grey01 tint on the full icon+label action.
  const { isHovered: isAddHovered, hoverProps: addHoverProps } = useHover();
  const { isHovered: isCopyHovered, hoverProps: copyHoverProps } = useHover();

  const [createTeamInvitationMutation, { data: createTeamInvitationData }] =
    useCreateTeamInvitationMutation();

  useEffect(() => {
    const link = createTeamInvitationData?.createTeamInvitation;
    if (!link) return;
    const formattedLink =
      link.replace('register/register-with-email', 'invited-user') +
      `&type=team`;
    copyToClipboard(formattedLink);
  }, [createTeamInvitationData]);

  const [completeWebTutorialFlows] = useCompleteWebTutorialFlowsMutation({
    refetchQueries: [{ query: GetMeDocument }, 'getMe'],
  });

  const copyToClipboard = async (link: string) => {
    if (!link) return; // Early return if link is falsy
    const inviteLink = `You’re invited to join our new team at ${teamName}. Click the link below to get started: `;
    await Clipboard.setStringAsync(inviteLink + link);
    navigator.share({
      title: inviteLink,
      url: link,
    });

    toast.show('Copied', {
      placement: 'center',
      duration: 3000,
    });
  };

  return (
    <Box flexDirection='row' alignItems='center' mx='m' mt='xs' mb='m'>
      {isOwnerOrAdmin && (
        <TouchableOpacity
          onPress={() => {
            setShowAddMembersModal(true);
          }}
          style={styles.marginRight}>
          <Box
            {...addHoverProps}
            accessibilityLabel={t('shared:addMembers')}
            flexDirection='row'
            alignItems='center'
            padding='xxs'
            borderRadius='xs'
            backgroundColor={isAddHovered ? 'grey01' : undefined}>
            <Icon name='UserPlus' variant='l' backgroundColor='grey01' />
            <Text variant='labelEmphasized' ml='xs'>
              {t('shared:addMembers')}
            </Text>
          </Box>
        </TouchableOpacity>
      )}

      <TouchableOpacity
        onPress={() => {
          if (!me?.completedWebTutorialFlows?.includes('invite_member')) {
            completeWebTutorialFlows({
              variables: {
                flowType: WebTutorialFlow.InviteMember,
              },
            });
          }
          createTeamInvitationMutation({ variables: { id: teamId } });
        }}
        style={styles.marginRight}>
        <Box
          {...copyHoverProps}
          accessibilityLabel={t('shared:copyLinkToInvite')}
          flexDirection='row'
          alignItems='center'
          padding='xxs'
          borderRadius='xs'
          backgroundColor={isCopyHovered ? 'grey01' : undefined}>
          <Icon name='Link' variant='l' backgroundColor='grey01' />
          <Text variant='labelEmphasized' ml='xs'>
            {t('shared:copyLinkToInvite')}
          </Text>
        </Box>
      </TouchableOpacity>
    </Box>
  );
};

const styles = StyleSheet.create({
  marginRight: {
    marginRight: theme.spacing.l,
  },
});

export default TeamActionList;
